import { useEffect, useState, type ReactElement } from 'react';
import { taxApi, type TaxHistoryEntry } from '../../../lib/api';
import { ErrorNote } from '../../ui';

// ---- Types ----------------------------------------------------------------

interface Props {
  companyId: string;
  type?: string;
  /** Aenderung dieses Werts erzwingt ein Neuladen (z.B. nach Uebermittlung). */
  refreshKey?: number;
}

// ---- Status badge ---------------------------------------------------------

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  submitting: {
    label: 'In Übermittlung',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  submitted: {
    label: 'Übermittelt',
    cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  accepted: {
    label: 'Angenommen',
    cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  rejected: {
    label: 'Abgelehnt',
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
  extern_eingereicht: {
    label: 'Extern eingereicht',
    cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
};

function HistoryBadge({ status }: { status: string }): ReactElement {
  const s = STATUS_MAP[status] ?? { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>
  );
}

// ---- Date formatter -------------------------------------------------------

function fmtDate(iso: string | null): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---- Download helper ------------------------------------------------------

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ---- Copy button with transient "Kopiert" state ---------------------------

function CopyButton({ text }: { text: string }): ReactElement {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors in restricted environments
    }
  }

  return (
    <button
      type="button"
      onClick={() => { void handleCopy(); }}
      className="ml-1 rounded px-2 py-0.5 text-xs font-medium ring-1 ring-gray-300 hover:bg-gray-100 dark:ring-gray-600 dark:hover:bg-gray-700"
    >
      {copied ? 'Kopiert ✓' : 'Kopieren'}
    </button>
  );
}

// ---- Main component -------------------------------------------------------

export function SubmissionHistory({ companyId, type, refreshKey }: Props): ReactElement {
  const [entries, setEntries] = useState<TaxHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    taxApi
      .history(companyId, type)
      .then((data) => {
        if (!cancelled) {
          setEntries(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [companyId, type, refreshKey]);

  // Nach einer Uebermittlung die Historie automatisch oeffnen (Sichtbarkeit).
  useEffect(() => {
    if (refreshKey) setOpen(true);
  }, [refreshKey]);

  async function handleDownloadProtocol(entry: TaxHistoryEntry) {
    setDownloading(entry.id);
    try {
      const blob = await taxApi.protocolPdf(companyId, entry.id);
      const filename = `ELSTER-Protokoll-${entry.type}-${entry.period}.pdf`;
      downloadBlob(blob, filename);
    } catch (err) {
      setError(err);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <span>Versandhistorie anzeigen</span>
        <span className="text-gray-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {error != null && (
            <div className="p-4">
              <ErrorNote error={error} />
            </div>
          )}

          {loading && (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">Lade Verlauf …</p>
          )}

          {!loading && !error && entries.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
              Noch keine Übermittlungen für diese Steuerart.
            </p>
          )}

          {!loading && entries.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Zeitraum
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Datum
                    </th>
                    <th className="hidden px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:table-cell">
                      Transfer-Ticket
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Aktion
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {entry.period}
                      </td>
                      <td className="px-4 py-3">
                        <HistoryBadge status={entry.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {fmtDate(entry.submittedAt)}
                      </td>
                      <td className="hidden px-4 py-3 sm:table-cell">
                        {entry.transferTicket ? (
                          <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
                            {entry.transferTicket}
                          </span>
                        ) : (
                          <span className="text-gray-400">–</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {entry.hasProtocol && (
                            <button
                              type="button"
                              disabled={downloading === entry.id}
                              onClick={() => { void handleDownloadProtocol(entry); }}
                              className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-60 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                            >
                              {downloading === entry.id ? (
                                <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-400/40 border-t-blue-600" />
                              ) : null}
                              Protokoll (PDF)
                            </button>
                          )}
                          {entry.status === 'rejected' && entry.error?.supportRef && (
                            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                              Ref:
                              <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
                                {entry.error.supportRef}
                              </span>
                              <CopyButton text={entry.error.supportRef} />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
